/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: languageUtils.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the PBX Incoming Call Tools module.
────────────────────────────────────────────────────────────────*/


import { useLanguageOptions } from '../../hooks/useLanguageOptions';

export const deepClone = <T,>(value: T): T =>
  typeof structuredClone === 'function'
    ? structuredClone(value)
    : JSON.parse(JSON.stringify(value));

export const generateContextFromName = (rawName: string) =>
  rawName
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .substring(0, 80)
    .replace(/^lang_/, '')
    .replace(/^/, 'lang_');

export const convertSoundPathToSelectorValue = (soundPath: string): string => {
  if (!soundPath) return '';
  const parts = soundPath.split('/');
  if (parts.length >= 4 && parts[0] === 'default') {
    return `${parts[2]}-${parts[1]}-${parts[3]}`;
  }
  if (parts.length >= 3) {
    return `${parts[0]}-${parts[1]}-${parts[2]}`;
  }
  return '';
};

export const convertSelectorValueToSoundPath = (
  selectorValue: string,
  languageOptions: ReturnType<typeof useLanguageOptions>['languageOptions'],
  defaultSoundPrefix = 'default/us/en/callie',
): string => {
  if (!selectorValue) return defaultSoundPrefix;
  const [language, region, voice] = selectorValue.split('-');
  if (!language || !region) return defaultSoundPrefix;

  const languageOption = languageOptions.find((opt) => opt.value === `${language}-${region}`);
  if (!languageOption) {
    return `${language}/${region}/${voice ?? 'callie'}`;
  }
  const voiceOption = voice
    ? languageOption.voices.find((v) => v.value === voice)
    : languageOption.voices[0];
  if (!voiceOption) {
    return `${language}/${region}/${voice ?? 'callie'}`;
  }
  if (!voiceOption.fullPath) {
    return `${language}/${region}/${voice ?? voiceOption.value}`;
  }
  const match = voiceOption.fullPath.match(/sounds\/(.+?)\/?$/);
  return match?.[1]?.replace(/\/$/, '') ?? `${language}/${region}/${voiceOption.value}`;
};
